/**
 * v4.13.0 — Schedule Snapshot Timeline (visual orchestration layer).
 *
 * Renders a card's daily timing schedule as a banded ruler with
 * platform-colored segments and AM/PM tick labels. Replaces the older
 * thin strip with a richer "command-center" look that mirrors the
 * Fleet Schedule Overview pattern: same visual vocabulary across the
 * page.
 *
 * Contracts preserved from the previous component:
 *   - Disabled / no-schedule state shows static destination dot.
 *   - Same-day blocks render as one segment.
 *   - Crossover blocks (start > end) render as two segments (evening
 *     + morning) so 22:00 → 08:00 reads correctly.
 *   - Block titles surface the range + platform on hover.
 */
import { useTranslation } from "react-i18next";
import PlatformIcon, { platformColor } from "./PlatformIcon";

const RULER_TICKS = [
    { hour: 5,  label: "5AM" },
    { hour: 12, label: "12PM" },
    { hour: 18, label: "6PM" },
    { hour: 24, label: "Close" },
];

const toMin = (hhmm) => {
    if (!hhmm) return 0;
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
};

const platformLabel = (p, t) =>
    t(`card_fleet.platforms.${p}`, { defaultValue: p || "—" });

const TickRuler = () => (
    <div
        className="relative h-3 mb-1 text-[9px] uppercase tracking-wider text-slate-500"
        aria-hidden
    >
        {RULER_TICKS.map((tk) => (
            <span
                key={tk.hour}
                className="absolute"
                style={{
                    left: `${(tk.hour / 24) * 100}%`,
                    transform: tk.hour === 24 ? "translateX(-100%)" : (tk.hour === 0 ? "translateX(0)" : "translateX(-50%)"),
                    whiteSpace: "nowrap",
                }}
            >
                {tk.label}
            </span>
        ))}
    </div>
);

export default function ScheduleSnapshot({ schedule, fallbackPlatform }) {
    const { t } = useTranslation();

    // ── Static / disabled state ──
    if (!schedule || !schedule.enabled) {
        return (
            <div
                className="flex items-center gap-2 text-xs text-slate-400"
                data-testid="schedule-snapshot-static"
            >
                <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: platformColor(fallbackPlatform) }}
                />
                <span className="truncate">
                    {t("card_fleet.schedule.static", {
                        platform: platformLabel(fallbackPlatform, t),
                    })}
                </span>
            </div>
        );
    }

    const blocks = (schedule.blocks || []).filter((b) => b.start && b.end);
    if (blocks.length === 0) {
        return (
            <div className="text-xs text-slate-500" data-testid="schedule-snapshot-empty">
                {t("card_fleet.schedule.no_blocks")}
            </div>
        );
    }

    // Expand crossover blocks into two segments so the ruler reads correctly.
    const expanded = [];
    blocks.forEach((b, i) => {
        const s = Math.max(0, toMin(b.start));
        const e = Math.min(24 * 60, toMin(b.end));
        if (s === e) return;
        if (s < e) {
            expanded.push({ s, e, platform: b.platform, range: `${b.start}–${b.end}`, key: `${i}-1` });
        } else {
            expanded.push({ s, e: 24 * 60, platform: b.platform, range: `${b.start}–24:00`, key: `${i}-1` });
            expanded.push({ s: 0, e, platform: b.platform, range: `00:00–${b.end}`, key: `${i}-2` });
        }
    });
    const sorted = expanded.sort((a, b) => a.s - b.s);

    // Build segs (block + gap) with cumulative cursor for absolute positioning.
    const segs = [];
    let cursor = 0;
    sorted.forEach((b) => {
        if (b.s > cursor) {
            segs.push({ kind: "gap", from: cursor, to: b.s });
        }
        segs.push({
            kind: "block",
            from: b.s,
            to: b.e,
            platform: b.platform,
            label: platformLabel(b.platform, t),
            range: b.range,
            key: b.key,
        });
        cursor = b.e;
    });
    if (cursor < 24 * 60) {
        segs.push({ kind: "gap", from: cursor, to: 24 * 60 });
    }

    return (
        <div className="w-full min-w-0" data-testid="schedule-snapshot-active">
            <TickRuler />
            <div
                className="relative flex items-stretch h-6 rounded-md overflow-hidden"
                style={{
                    background: "rgba(148, 163, 184, 0.06)",
                    border: "1px solid rgba(148, 163, 184, 0.14)",
                }}
            >
                {segs.map((s, idx) => {
                    const widthPct = ((s.to - s.from) / (24 * 60)) * 100;
                    if (s.kind === "gap") {
                        return (
                            <div
                                key={`g-${idx}`}
                                style={{ width: `${widthPct}%` }}
                            />
                        );
                    }
                    const color = platformColor(s.platform);
                    return (
                        <div
                            key={s.key || `b-${idx}`}
                            title={`${s.label} · ${s.range}`}
                            data-testid={`schedule-seg-${s.platform}`}
                            className="relative flex items-center justify-center text-[10px] font-semibold text-white truncate px-1"
                            style={{
                                width: `${widthPct}%`,
                                background: `linear-gradient(180deg, ${color}, ${color}d9)`,
                                minWidth: "10px",
                                boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.18)",
                            }}
                        >
                            {widthPct > 14 ? (
                                <span style={{ textShadow: "0 1px 0 rgba(0,0,0,0.25)" }}>
                                    {s.label}
                                </span>
                            ) : null}
                        </div>
                    );
                })}
                {/* Vertical tick lines for orientation */}
                {RULER_TICKS.slice(0, -1).map((tk) => (
                    <div
                        key={`vl-${tk.hour}`}
                        className="absolute top-0 bottom-0 pointer-events-none"
                        style={{
                            left: `${(tk.hour / 24) * 100}%`,
                            width: "1px",
                            background: "rgba(15, 23, 42, 0.45)",
                            mixBlendMode: "overlay",
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
